import { createContext, useContext, useEffect, useState } from "react";

/**
 * Theme system:
 * - mode: light | dark
 * - accent: brand hue (HSL degrees 0-360) — updates --brand var
 * - radius: sm | md | lg (updates --radius via .density-* class)
 */
const ACCENTS = [
  { id: "blue",    hsl: "221 100% 50%", label: "Blue" },
  { id: "violet",  hsl: "262 83% 58%",  label: "Violet" },
  { id: "emerald", hsl: "152 74% 40%",  label: "Emerald" },
  { id: "amber",   hsl: "38 92% 50%",   label: "Amber" },
  { id: "rose",    hsl: "340 75% 55%",  label: "Rose" },
  { id: "slate",   hsl: "240 6% 20%",   label: "Slate" },
];

const DENSITIES = [
  { id: "compact", label: "Compact" },
  { id: "cozy",    label: "Cozy" },
  { id: "comfy",   label: "Comfy" },
];

const ThemeCtx = createContext({
  theme: "light", setTheme: () => {},
  accent: "blue", setAccent: () => {},
  density: "cozy", setDensity: () => {},
  accents: ACCENTS, densities: DENSITIES,
});

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem("bos_theme") || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  });
  const [accent, setAccent] = useState(() => (typeof window !== "undefined" && localStorage.getItem("bos_accent")) || "blue");
  const [density, setDensity] = useState(() => (typeof window !== "undefined" && localStorage.getItem("bos_density")) || "cozy");

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    localStorage.setItem("bos_theme", theme);
  }, [theme]);

  useEffect(() => {
    const a = ACCENTS.find(x => x.id === accent) || ACCENTS[0];
    document.documentElement.style.setProperty("--brand", a.hsl);
    localStorage.setItem("bos_accent", accent);
  }, [accent]);

  useEffect(() => {
    const root = document.documentElement;
    DENSITIES.forEach(d => root.classList.remove(`density-${d.id}`));
    root.classList.add(`density-${density}`);
    localStorage.setItem("bos_density", density);
  }, [density]);

  return (
    <ThemeCtx.Provider value={{ theme, setTheme, accent, setAccent, density, setDensity, accents: ACCENTS, densities: DENSITIES }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
